import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom'
import {
  CONTENT_PAGE_ROUTES,
  DNA_APP_BASE_PATH,
  DNA_APP_ROUTES,
  DNA_LEGACY_APP_BASE_PATH,
  PAGE_TYPES,
  getCanonicalDnaPathFromLegacyPath,
} from '../config/routes'

const Home = lazy(() => import("./pages/home"))
const Pages = lazy(() => import('./Pages'))
const AboutPage = lazy(() => import('./pages/about'))
const PolicyPage = lazy(() => import('./pages/policies'))
const TermsPage = lazy(() => import('./pages/terms'))
const DnaRouteView = lazy(() => import('../apps/dna/DnaRouteView'))

const renderContentRoute = (type) => {
  if (type === PAGE_TYPES.ABOUT) {
    return <AboutPage />
  }

  if (type === PAGE_TYPES.POLICY) {
    return <PolicyPage />
  }

  if (type === PAGE_TYPES.TERMS) {
    return <TermsPage />
  }

  return <Pages type={type} />
}

function ScrollBehavior() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) {
      return undefined
    }

    const previousValue = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'

    return () => {
      window.history.scrollRestoration = previousValue
    }
  }, [])

  return null
}

function RootLayout() {
  return (
    <>
      <ScrollBehavior />
      <Suspense fallback={<div className="App" />}>
        <Outlet />
      </Suspense>
    </>
  )
}

function LegacyDnaRedirect() {
  const location = useLocation()

  return (
    <Navigate
      to={getCanonicalDnaPathFromLegacyPath(location.pathname, location.search, location.hash)}
      replace
    />
  )
}

function CanonicalShadowPreviewRedirect() {
  const location = useLocation()
  const normalizedBase = `${DNA_APP_BASE_PATH}/canonical-shadow-preview`
  const normalizedPathname = String(location.pathname || '').replace(/\/+$/, '')
  const rawSuffix = normalizedPathname.startsWith(normalizedBase)
    ? normalizedPathname.slice(normalizedBase.length).replace(/^\/+/, '')
    : ''
  const allowedRoot = ['elements', 'tasks', 'progress', 'meals'].find((candidate) => (
    rawSuffix === candidate || rawSuffix.startsWith(`${candidate}/`)
  ))
  const targetSuffix = allowedRoot ? rawSuffix : ''
  const params = new URLSearchParams(location.search || '')

  params.delete('sentinel')
  params.delete('patient')
  params.delete('orderId')

  const query = params.toString()
  const target = `${DNA_APP_BASE_PATH}${targetSuffix ? `/${targetSuffix}` : ''}${query ? `?${query}` : ''}${location.hash || ''}`

  return <Navigate to={target} replace />
}

const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      ...CONTENT_PAGE_ROUTES.map(({ path, type }) => ({
        path,
        element: renderContentRoute(type),
      })),
      { path: DNA_LEGACY_APP_BASE_PATH, element: <LegacyDnaRedirect /> },
      { path: `${DNA_LEGACY_APP_BASE_PATH}/*`, element: <LegacyDnaRedirect /> },
      { path: `${DNA_APP_BASE_PATH}/canonical-shadow-preview`, element: <CanonicalShadowPreviewRedirect /> },
      { path: `${DNA_APP_BASE_PATH}/canonical-shadow-preview/*`, element: <CanonicalShadowPreviewRedirect /> },
      ...DNA_APP_ROUTES.map(({ path, type }) => ({
        path,
        element: <DnaRouteView routeType={type} />,
      })),
    ],
  },
])

function AppRouter() {
  return <RouterProvider router={appRouter} fallbackElement={<div className="App" />} />
}

export default AppRouter
